/*
 * Created on Thu Oct 12 2023 10:24:57 AM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */
import { AlertCircle, CheckCircle, Eye, EyeOff, LogIn } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { confirmUser, updatePassword } from "../../api/action/user.jsx";
import StyledTooltip from '../../components/StyledTooltip.jsx';

const ActivatedUser = (props) => {
  const { confirmUser: confirmUserAction, updatePassword: updatePasswordAction } = props;
  const [flag, setFlag] = useState(false);
  const [error, setError] = useState("");
  const [, setStatus] = useState(null);
  const [istrigered, setIsTrigered] = useState(false);
  const [expiredLinkError, setExpiredLinkError] = useState(null);
  
  const [, setShowComponent] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(true);
  const [, setToken] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordRules, setPasswordRules] = useState();

  const [userData, setUserData] = useState({
    email: '',
    newPassword: '',
    confirmPassword: ''
  });

  const navigate = useNavigate();

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    const checkPasswordValidity = (pwd) => {
      if (!pwd) return false;
      const rules = [
        { text: "At least 8 characters", valid: pwd.length >= 8 },
        { text: "Spaces not allowed", valid: !/\s/.test(pwd) },
        { text: "1 uppercase letter (A-Z)", valid: /[A-Z]/.test(pwd) },
        { text: "1 lowercase letter (a-z)", valid: /[a-z]/.test(pwd) },
        { text: "1 number (0-9)", valid: /[0-9]/.test(pwd) },
        { text: "1 special character (!@#$)", valid: /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(pwd) },
      ];
      return rules.every(rule => rule.valid);
    };

    setIsPasswordValid(checkPasswordValidity(password));

    const formattedRules = (checkPasswordValidity(password)) ? "" : `Password must contain:\n${[
      { text: "At least 8 characters", valid: password.length >= 8 },
      { text: "Spaces not allowed", valid: !/\s/.test(password) },
      { text: "1 uppercase letter (A-Z)", valid: /[A-Z]/.test(password) },
      { text: "1 lowercase letter (a-z)", valid: /[a-z]/.test(password) },
      { text: "1 number (0-9)", valid: /[0-9]/.test(password) },
      { text: "1 special character (!@#$)", valid: /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(password) },
    ].map((rule) => `${rule.valid ? "✓" : "○"} ${rule.text}`).join("\n")
    }`;

    setPasswordRules(formattedRules);

  }, [password]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "newPassword") {
      setPassword(value);
      setUserData(prev => ({ ...prev, newPassword: value }));
    } else if (name === "confirmPassword") {
      setConfirmPassword(value);
      setUserData(prev => ({ ...prev, confirmPassword: value }));
    } else {
      setUserData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!isPasswordValid) {
      setError("Please ensure your password meets all the criteria.");
      return;
    }

    const updatedUser = {
      ...userData,
      password: password,
      modifiedBy: userData.firstName + " " + userData.lastName,
    };

    updatePasswordAction(updatedUser)
      .then((res) => {
        if (res.message === "Password updated") {
          setFlag(true);
          localStorage.removeItem("reqUser");
          localStorage.removeItem("reqToken");
        }
        setSuccessMessage(true);
      })
      .catch((err) => {
        setFlag(false);
        setError(err?.message || err || "Failed to update password");
      });
  };

  const handleConfirm = useCallback((token) => {
    setIsTrigered(true);
    setExpiredLinkError(null);

    confirmUserAction(token)
      .then((response) => response.json())
      .then((data) => {
        if (data.status === "Failed" && data.message?.includes("expired")) {
          setExpiredLinkError({
            message: data.message,
            severity: "warning",
            correlationId: data.correlationId
          });
          return;
        }
        
        setStatus(data?.data?.status);

        const userRoles = data?.data?.userRoles || [];
        const password = data?.data?.password;

        const hasPassword = password != null && password !== '';
        const noPassword = password == null || password === '';

        if (userRoles.includes("STUDENT") || (userRoles.includes("CUSTOMER") && hasPassword)) {
          setFlag(true);
          setSuccessMessage(true);
          setTimeout(() => navigate("/sign-in"), 5000);
        } else if (
          (userRoles.includes("CUSTOMER") && noPassword) ||
          userRoles.includes("INSTRUCTOR") ||
          userRoles.includes("ADMIN") ||
          userRoles.includes("EMPLOYEE")
        ) {
          setFlag(true);
          setShowComponent(true);

          localStorage.setItem("reqToken", token);
          localStorage.setItem("reqUser", data?.data?.email);

          const reqUser = localStorage.getItem("reqUser");
          setUserData((prevUser) => ({
            ...prevUser,
            email: reqUser,
            ...data?.data,
          }));
        } else if (data.message) {
          setError(data.message);
        } else {
          throw new Error("Invalid response received from the server.");
        }
      })
      .catch((error) => {
        if (error.message?.includes("expired") || error.toString().includes("expired")) {
          setExpiredLinkError({
            message: "Your activation link has expired. Please request a new one.",
            severity: "warning"
          });
        } else {
          setError(error.message || "An error occurred during activation.");
        }
      });
  }, [confirmUserAction, navigate]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const term = queryParams.get("token");

    if (term) {
      setToken(term);
      if (!istrigered) {
        handleConfirm(term);
      }
      return;
    }

    const cond = localStorage.getItem("isLoggedIn");
    if (cond) {
      navigate("/products");
    }
  }, [handleConfirm, istrigered, navigate]);

  return (
    <main className="flex min-h-screen w-full items-center justify-center bg-gradient-to-br from-indigo-500 to-violet-700 p-4">
      <section className="w-full max-w-[500px] rounded-2xl bg-white p-8 text-center shadow-2xl shadow-slate-950/30 sm:p-10">
        {expiredLinkError && (
          <div className="mb-8 rounded-xl border border-amber-200 bg-amber-50 p-4 text-left text-amber-900">
            <div className="flex gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
              <div>
                <h2 className="font-semibold">Activation Link Expired</h2>
                <p className="mt-1 text-sm text-amber-800">
                  Oops! Your activation link has expired. We’ve sent a new activation link to your email.
                </p>
                {expiredLinkError.correlationId && (
                  <p className="mt-3 text-[10px] text-amber-700/70">
                    Reference ID: {expiredLinkError.correlationId}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {flag ? (
          successMessage ? (
            <div>
              <div className="mb-6 flex flex-col items-center gap-3">
                <CheckCircle className="h-12 w-12 text-green-500" />
                <h1 className="text-lg font-semibold text-slate-900">
                  Account Activated Successfully!
                </h1>
              </div>
              <p className="mb-8 text-slate-700">
                Your account has been verified. Click below to sign in.
              </p>
              <a
                href="/sign-in"
                className="inline-flex items-center justify-center gap-2 font-medium text-blue-600 transition hover:text-blue-700 hover:underline"
              >
                <LogIn className="h-4 w-4" />
                <span>Sign In to Your Account</span>
              </a>
            </div>
          ) : (
            <div>
              <h1 className="mb-3 text-2xl font-semibold text-slate-900">
                Set Your Password
              </h1>
              <p className="mb-8 text-sm text-slate-500">
                Please create a secure password to continue
              </p>

              <form onSubmit={handleSubmit}>
                <div className="mx-auto flex w-full max-w-[300px] flex-col items-stretch gap-5">
                  <label className="block text-left">
                    <span className="mb-1 block text-sm font-medium text-slate-700">Email</span>
                    <input
                      name="email"
                      value={userData.email}
                      disabled
                      type="email"
                      required
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-600 outline-none"
                    />
                  </label>

                  <StyledTooltip className="w-full" title={passwordRules} placement="left" arrow>
                    <label className="relative block w-full text-left">
                      <span className="mb-1 block text-sm font-medium text-slate-700">Password</span>
                      <input
                        name="newPassword"
                        value={userData.newPassword}
                        onChange={handleChange}
                        type={showPassword ? "text" : "password"}
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 pr-10 text-sm text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                        placeholder="Enter your password"
                      />
                      <button
                        type="button"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        className="absolute right-2 top-[30px] rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </label>
                  </StyledTooltip>

                  <label className="relative block text-left">
                    <span className="mb-1 block text-sm font-medium text-slate-700">Confirm Password</span>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={userData.confirmPassword}
                      required
                      name="confirmPassword"
                      onChange={handleChange}
                      className={`w-full rounded-lg border bg-slate-50 px-3 py-2.5 pr-10 text-sm text-slate-900 outline-none transition focus:ring-2 ${
                        isSubmitted && userData.confirmPassword !== userData.newPassword
                          ? "border-red-400 focus:border-red-400 focus:ring-red-100"
                          : "border-slate-200 focus:border-blue-400 focus:ring-blue-100"
                      }`}
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      onClick={handleClickShowConfirmPassword}
                      onMouseDown={handleMouseDownPassword}
                      className="absolute right-2 top-[30px] rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                      aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </label>

                  {isSubmitted && userData.confirmPassword !== userData.newPassword && (
                    <p className="text-left text-sm text-red-500">Passwords do not match</p>
                  )}

                  <button
                    type="submit"
                    disabled={!isPasswordValid}
                    className={`mt-1 w-full rounded-lg px-4 py-3 text-base font-semibold text-white shadow transition ${
                      isPasswordValid
                        ? "bg-gradient-to-br from-blue-600 to-blue-500 shadow-blue-600/30 hover:scale-[1.02]"
                        : "cursor-not-allowed bg-slate-300 shadow-none"
                    }`}
                  >
                    Save Password
                  </button>
                </div>
              </form>
            </div>
          )
        ) : (
          !expiredLinkError && error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-left text-red-800">
              <div className="flex gap-3">
                <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
                <div>
                  <h2 className="font-semibold">Error</h2>
                  <p className="mt-1 text-sm">{error}</p>
                </div>
              </div>
            </div>
          )
        )}

        {!flag && !expiredLinkError && !error && (
          <div className="mt-6">
            <a
              href="/sign-in"
              className="inline-flex items-center gap-2 text-sm text-slate-500 transition hover:text-blue-600"
            >
              <LogIn className="h-4 w-4" />
              <span>Back to Sign In</span>
            </a>
          </div>
        )}
      </section>
    </main>
  );
};

function mapStateToProps(state) {
  const isAuthenticated = state?.auth?.data?.isAuthenticated ?? false;
  const user = state?.auth?.data?.user ?? null;

  return {
    isAuthenticated: isAuthenticated,
    user: user
  };
}

export default connect(mapStateToProps, { confirmUser, updatePassword })(ActivatedUser);
